/* eslint-disable camelcase */
import { capitalizeString, DateFormats, formatDate } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { TextInputFormik } from '@strata/react-inputs';
import { HomeScreen } from '@strata/react-page-templates';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
    Application,
    ApplicationDisplayTextMap,
} from 'pages/homeScreenMessaging/HomeScreenHelpers';

export const MessageCategoryInput = ({ label }) => (
    <TextInputFormik
        id="message_category"
        label={label}
        helperText="Used only in Lucent."
        required
    />
);
MessageCategoryInput.propTypes = { label: PropTypes.string.isRequired };
MessageCategoryInput.initialValues = (initialValues) => ({
    message_category: initialValues?.message_category,
});
MessageCategoryInput.validationSchema = {
    message_category: Yup.string().required(validationStrings.requiredField.errorMessage),
};
MessageCategoryInput.submitValues = (values) => ({ message_category: values.message_category });

export const PublishDateInput = ({ messageType }) => (
    <DateInputFormik
        id="publish_date"
        label="Publish Date"
        helperText={`${capitalizeString(messageType)} will be published at 12:00 AM on this date.`}
        required
    />
);
PublishDateInput.propTypes = {
    messageType: PropTypes.oneOf(Object.values(HomeScreen.MessageType)).isRequired,
};
PublishDateInput.initialValues = (initialValues) => ({
    publish_date: initialValues?.publish_date ?? dayjs().toISOString(),
});
PublishDateInput.validationSchema = {
    publish_date: DateInputFormik.validator().required(
        validationStrings.requiredField.errorMessage,
    ),
};
PublishDateInput.submitValues = (values) => ({
    publish_date: formatDate(values.publish_date, DateFormats.HYPHEN_DATE),
});

export const EndDateInput = ({ messageType }) => (
    <DateInputFormik
        id="end_date"
        label="End Date"
        helperText={`${capitalizeString(messageType)} will be removed at 11:59 PM on this date.`}
        required
    />
);
EndDateInput.propTypes = {
    messageType: PropTypes.oneOf(Object.values(HomeScreen.MessageType)).isRequired,
};
EndDateInput.initialValues = (initialValues) => ({
    end_date: initialValues?.end_date,
});
EndDateInput.validationSchema = {
    end_date: DateInputFormik.validator().required(validationStrings.requiredField.errorMessage),
};
EndDateInput.submitValues = (values) => ({
    end_date: formatDate(values.end_date, DateFormats.HYPHEN_DATE),
});

const ApplicationOptions = Object.values(Application)
    .filter((app) => app !== Application.EXTERNAL)
    .map((app) => ({
        application: app,
        name: ApplicationDisplayTextMap[app],
    }));

export const ApplicationInputs = () => {
    const formik = useFormikContext();
    return (
        <>
            <RadioButtonGroupFormik
                name="is_all_apps"
                label="Application"
                options={[
                    {
                        value: true,
                        text: 'All External Applications (FMH for Payers, FMH for Pharmacies, RTA)',
                    },
                    { value: false, text: 'Single Application' },
                ]}
                onChange={(newValue) => formik.setFieldValue('is_all_apps', newValue === 'true')}
                required
            />
            {formik.values.is_all_apps === false && (
                <SuggestSingleSelectFormik
                    id="application"
                    label="Application"
                    values={ApplicationOptions}
                    required
                />
            )}
        </>
    );
};
ApplicationInputs.initialValues = (initialValues) => ({
    is_all_apps: initialValues?.application
        ? initialValues.application === Application.EXTERNAL
        : undefined,
    application: ApplicationOptions.find((app) => app.application === initialValues?.application),
});
ApplicationInputs.validationSchema = {
    is_all_apps: Yup.bool().required(validationStrings.requiredField.errorMessage),
    application: Yup.object().when('is_all_apps', {
        is: (is_all_apps) => !is_all_apps,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
};
ApplicationInputs.submitValues = (values) => ({
    application: values.is_all_apps ? Application.EXTERNAL : values.application?.application,
});

export const DisplayInput = () => {
    const formik = useFormikContext();
    return (
        <RadioButtonGroupFormik
            name="display"
            label="Display"
            options={[
                { value: true, text: 'On' },
                { value: false, text: 'Off' },
            ]}
            onChange={(newValue) => formik.setFieldValue('display', newValue === 'true')}
            required
        />
    );
};
DisplayInput.initialValues = (initialValues) => ({ display: initialValues?.display ?? true });
DisplayInput.validationSchema = {
    display: Yup.bool().required(validationStrings.requiredField.errorMessage),
};
DisplayInput.submitValues = (values) => ({ display: values.display });
